import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { MatchKey_Read, ModelError_Response } from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { M2MClient } from '~hooks/useApiClients/models';
import { MatchKey } from '~hooks/useMatchKeys/models';
import useMatchKeys from '~hooks/useMatchKeys/useMatchKeys';
import useCompanyApiClientsQuery from '~queries/useCompanyApiClientsQuery';
import { COMPANY_MATCH_KEYS_QUERY_KEY } from '~queries/useCompanyMatchKeysQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import AddMatchKeyDialog, { SourceOptionsStatus } from './AddMatchKeyDialog';
import MatchKeyList from './MatchKeyList';

interface MatchKeysSectionProps {
  companyId?: string;
  matchableId?: string;
  matchableType: MatchKey_Read['matchable_type'];
  recordName: string;
}

const MatchKeysSection = observer(
  ({ companyId, matchableId, matchableType, recordName }: MatchKeysSectionProps) => {
    const { toasterStore, userStore } = useStores();
    const userPermissions = userStore.getPermissions();
    const matchKeysEnabled = useFeatureFlag({
      featureFlagKey: FeatureFlags.matchKeysEnabled,
    });

    const { createMatchKey, deleteMatchKey, fetchAllMatchKeys } = useMatchKeys();
    const queryClient = useQueryClient();

    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [matchKeys, setMatchKeys] = useState<MatchKey[]>([]);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [matchKeyToDelete, setMatchKeyToDelete] = useState<MatchKey | null>(null);

    const editedRecordKey = `${companyId}:${matchableType}:${matchableId}`;
    const editedRecordKeyRef = useRef(editedRecordKey);

    // Host dialogs stay mounted while the user switches records, so state kept here would
    // otherwise describe the previously edited record.
    useEffect(() => {
      editedRecordKeyRef.current = editedRecordKey;
      setIsExpanded(false);
      setIsLoading(false);
      setMatchKeys([]);
      setIsAddDialogOpen(false);
      setMatchKeyToDelete(null);
    }, [editedRecordKey]);

    const {
      apiClients,
      isError: hasSourceOptionsError,
      isFetching: isFetchingSourceOptions,
      refetch: refetchSourceOptions,
    } = useCompanyApiClientsQuery({
      companyId,
      isEnabled: isExpanded && Boolean(matchableId),
    });

    const sourceOptions = useMemo<AutocompleteFieldOption<M2MClient>[]>(
      () =>
        apiClients
          .filter((apiClient) => Boolean(apiClient.slug))
          .map((apiClient) => ({
            _entity: apiClient,
            label: `${apiClient.slug} — ${apiClient.name}`,
            value: apiClient.slug ?? '',
          })),
      [apiClients],
    );

    const sourceOptionsStatus: SourceOptionsStatus = isFetchingSourceOptions
      ? 'loading'
      : hasSourceOptionsError
        ? 'error'
        : 'ready';

    const reloadMatchKeys = useCallback(async () => {
      if (!companyId || !matchableId) {
        return;
      }

      const requestedRecordKey = editedRecordKey;
      const fetchedMatchKeys = await fetchAllMatchKeys({
        companyId,
        matchableId,
        matchableType,
      });

      if (editedRecordKeyRef.current === requestedRecordKey) {
        setMatchKeys(fetchedMatchKeys);
      }
    }, [companyId, editedRecordKey, fetchAllMatchKeys, matchableId, matchableType]);

    // The entity table reads a separate company-wide query, which cannot see a key saved
    // through this section's own endpoint.
    const handleMatchKeysMutated = useCallback(async () => {
      await Promise.all([
        reloadMatchKeys(),
        queryClient.invalidateQueries({ queryKey: [COMPANY_MATCH_KEYS_QUERY_KEY] }),
      ]);
    }, [queryClient, reloadMatchKeys]);

    const handleToggleExpanded = useCallback(async () => {
      const willExpand = !isExpanded;

      setIsExpanded(willExpand);

      const shouldLoadMatchKeys = willExpand && Boolean(matchableId);

      if (!shouldLoadMatchKeys) {
        return;
      }

      setIsLoading(true);

      try {
        await reloadMatchKeys();
      } finally {
        setIsLoading(false);
      }
    }, [isExpanded, matchableId, reloadMatchKeys]);

    const handleCreateMatchKey = useCallback(
      async (key: string) => {
        if (!companyId || !matchableId) {
          return;
        }

        setIsSubmitting(true);

        try {
          await createMatchKey({ companyId, key, matchableId, matchableType });
          setIsAddDialogOpen(false);
          await handleMatchKeysMutated();
        } catch (error) {
          // The hook re-throws 422 without toasting so the duplicate-key message
          // reaches the user here; everything else it has already reported.
          if (isAxiosError(error) && error.response?.status === 422) {
            const validationError = error as AxiosError<ModelError_Response>;

            toasterStore.push(
              alert(
                validationError.response?.data.error.errors?.[0]?.message ??
                  t('error_messages.match_keys.failed_to_create_match_key'),
                AlertTypes.error,
              ),
            );
          }
        } finally {
          setIsSubmitting(false);
        }
      },
      [
        companyId,
        createMatchKey,
        handleMatchKeysMutated,
        matchableId,
        matchableType,
        toasterStore,
      ],
    );

    const handleDeleteMatchKey = useCallback(async () => {
      if (!companyId || !matchKeyToDelete) {
        return;
      }

      setIsSubmitting(true);

      try {
        await deleteMatchKey({ companyId, matchKeyId: matchKeyToDelete.id });
        setMatchKeyToDelete(null);
        await handleMatchKeysMutated();
      } catch {
        // Error is already handled in the hook.
      } finally {
        setIsSubmitting(false);
      }
    }, [companyId, deleteMatchKey, handleMatchKeysMutated, matchKeyToDelete]);

    if (!matchKeysEnabled || !userPermissions.canViewMatchKey) {
      return null;
    }

    return (
      <Box data-ui="match-keys__section">
        <Divider sx={{ my: 2 }} />

        <PseudoLink
          action={() => void handleToggleExpanded()}
          sx={{ display: 'inline-flex' }}
          title={
            isExpanded
              ? t('administration.match_keys.hide_match_keys')
              : t('administration.match_keys.title')
          }
        />

        <Collapse in={isExpanded}>
          <Box sx={{ mt: 1 }}>
            {!matchableId ? (
              <Typography sx={{ color: 'text.secondary' }}>
                {t('administration.match_keys.save_record_first')}
              </Typography>
            ) : isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <MatchKeyList
                  canDeleteMatchKey={userPermissions.canDeleteMatchKey}
                  matchKeys={matchKeys}
                  onDeleteMatchKey={setMatchKeyToDelete}
                />

                {userPermissions.canCreateMatchKey && (
                  <SmallButton
                    color="secondary"
                    data-ui="match-keys__section__add-button"
                    onClick={() => setIsAddDialogOpen(true)}
                    variant="outlined"
                  >
                    {t('administration.match_keys.add_match_key')}
                  </SmallButton>
                )}
              </>
            )}
          </Box>
        </Collapse>

        <AddMatchKeyDialog
          isOpen={isAddDialogOpen}
          isSubmitting={isSubmitting}
          onClose={() => setIsAddDialogOpen(false)}
          onRetryLoadSources={() => void refetchSourceOptions()}
          onSubmit={handleCreateMatchKey}
          recordName={recordName}
          sourceOptions={sourceOptions}
          sourceOptionsStatus={sourceOptionsStatus}
        />

        <ConfirmationDialog
          isBusy={isSubmitting}
          isOpen={Boolean(matchKeyToDelete)}
          onClose={() => setMatchKeyToDelete(null)}
          onConfirm={handleDeleteMatchKey}
          title={t('administration.match_keys.delete_match_key_title')}
        >
          <Typography>{t('administration.match_keys.delete_match_key_text')}</Typography>
        </ConfirmationDialog>
      </Box>
    );
  },
);

export default MatchKeysSection;
